import type { BootstrapResponse } from "../../../api/schemas";
import { isIsolatedLocalExecution } from "../../../api/action-capabilities";
import type { RedeemFeeRoute } from "../../../wallet/redeem-fee-quote";
import { sameAddress } from "../../../state/model-helpers";
import { ELYSIUM } from "../../../config/elysium";
import { selectElysiumDirectRedeem, type DirectRedeemRoute } from "../../../wallet/elysium-redeem";
export type RedeemRoute = RedeemFeeRoute | DirectRedeemRoute;
export function isDirectRedeemRoute(route: RedeemRoute | null): route is DirectRedeemRoute {
  return route?.kind === "ELYSIUM_DIRECT_VAULT" && route.support === "CONFIGURED_READ_ONLY";
}

/** Selection permits read-only observations; action capability/preflight still own send permission. */
export function selectRedeemRoute(bootstrap: BootstrapResponse | null, sourceToken: string,
  feeRoute: RedeemFeeRoute | null = null): RedeemRoute | null {
  if (bootstrap?.contracts.mintChainId === ELYSIUM.chainId) {
    if (feeRoute) throw new Error("Elysium redemption cannot use or fall back to a legacy fee route.");
    return selectElysiumDirectRedeem(bootstrap, sourceToken);
  }
  const local = isIsolatedLocalExecution(bootstrap);
  const readOnly = feeRoute?.support === "PINNED_RPC_READ_ONLY" && Boolean(feeRoute.observedDeployment);
  if (!local && !readOnly) throw new Error("A verified redemption route and backend permission are unavailable.");
  if (!feeRoute) return null; // Preserve the explicitly supported isolated legacy deployment.
  const token = bootstrap?.tokens.find(item => sameAddress(item.tokenAddress, sourceToken));
  if (!bootstrap || feeRoute.kind !== "REDEEM_WITH_FEES_CURVE_V1" ||
    !(readOnly || local && feeRoute.support === "ISOLATED_TEST_ONLY" && feeRoute.environment !== "production") ||
    feeRoute.environment !== bootstrap.environment ||
    feeRoute.chainId !== bootstrap.contracts.mintChainId || feeRoute.chainId !== bootstrap.deployment.executionChainId ||
    !sameAddress(feeRoute.collection, bootstrap.contracts.collectionAddress) ||
    !sameAddress(feeRoute.vault, bootstrap.contracts.rewardVaultAddress) ||
    (!readOnly || bootstrap.contracts.dedicatedFeeReceiverAddress !== null) &&
      !sameAddress(feeRoute.receiver, bootstrap.contracts.dedicatedFeeReceiverAddress) ||
    !sameAddress(feeRoute.sourceToken, sourceToken) || !sameAddress(feeRoute.curve, token?.curveAddress) ||
    token?.chainId !== feeRoute.chainId || !feeRoute.deploymentRevision.trim()) {
    throw new Error("Redemption route does not match the authenticated deployment. No legacy fallback is allowed.");
  }
  return Object.freeze({ ...feeRoute });
}
